import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export interface Notification {
  id: string;
  user_id: string;
  type: 'reminder_due' | 'task_overdue' | 'lead_inactive' | 'lead_stuck' | 'escalation' | 'assignment' | 'lead_status_change' | 'appointment_upcoming' | 'leads_on_inactive_user';
  title: string;
  description?: string;
  priority: 'normal' | 'high' | 'critical';
  read: boolean;
  resolved: boolean;
  resolved_at?: string;
  lead_id?: string;
  reminder_id?: string;
  task_id?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  lead?: {
    id: string;
    company: string;
  };
}

export function useNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const notifiedIdsRef = useRef<Set<string>>(new Set());

  // Fetch unread notifications
  const { data: notifications = [], isLoading, refetch } = useQuery({
    queryKey: ['notifications', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select(`
          *,
          lead:leads(id, company)
        `)
        .eq('user_id', user!.id)
        .eq('resolved', false)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as unknown as Notification[];
    },
    refetchInterval: 300000, // Fallback: 5 min (Realtime primary)
  });

  // Count unread
  const unreadCount = notifications.filter(n => !n.read).length;
  const criticalCount = notifications.filter(n => n.priority === 'critical' && !n.read).length;

  // Handle new notification toast
  const handleNewNotification = useCallback((payload: any) => {
    const notification = payload.new as Notification;
    
    // Avoid duplicate toasts
    if (notifiedIdsRef.current.has(notification.id)) return;
    notifiedIdsRef.current.add(notification.id);
    
    // Show toast for critical notifications
    if (notification.priority === 'critical') {
      toast.error('🚨 Alerte critique', {
        description: notification.title,
        duration: 15000,
        action: notification.lead_id ? {
          label: 'Voir',
          onClick: () => navigate(`/leads/${notification.lead_id}`),
        } : undefined,
      });
    } else if (notification.priority === 'high') {
      toast.warning('Nouvelle notification', {
        description: notification.title,
        duration: 8000,
        action: notification.lead_id ? {
          label: 'Voir',
          onClick: () => navigate(`/leads/${notification.lead_id}`),
        } : undefined,
      });
    }
    
    // Invalidate queries
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }, [navigate, queryClient]);

  // Subscribe to ALL realtime changes (INSERT, UPDATE, DELETE)
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('notifications-realtime-full')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        handleNewNotification
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, handleNewNotification, queryClient]);

  // Mark as read
  const markAsRead = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Mark all as read
  const markAllAsRead = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user!.id)
        .eq('read', false);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Mark as resolved
  const markAsResolved = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ resolved: true, resolved_at: new Date().toISOString() })
        .eq('id', notificationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  return {
    notifications,
    isLoading,
    unreadCount,
    criticalCount,
    markAsRead,
    markAllAsRead,
    markAsResolved,
    refetch,
  };
}
